import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { registerPluggableViewDeclaration, unregisterPluggableView } from './pluggableViewRegistry';
import PluggableView from './PluggableView';
/**
 * @export
 * Hook to register a pluggable view and get a render function
 *
 * Simplifies pluggable view usage by eliminating duplication:
 * - Registers the view declaration once
 * - Returns a render function for usage
 *
 * @param declaration - View declaration without props (props passed to render function)
 * @returns Render function that accepts props and returns PluggableView JSX
 *
 * @example
 * ```typescript
 * const renderOtherDetails = usePluggableView({
 *     viewId: `${App}/details/other`,
 *     parentViewId: `${App}/details`,
 *     defaultPage: OtherDetails
 * });
 *
 * // Use in render:
 * return (
 *     <div>
 *         {renderOtherDetails({
 *             location: item,
 *             loading,
 *             isPreview
 *         })}
 *     </div>
 * );
 * ```
 */
export function usePluggableView(declaration) {
    // Callers pass a fresh `declaration` object literal every render. Rebuild it from
    // its primitive fields so the effect and the returned render function stay stable
    // across renders — otherwise every parent render re-registers the view AND returns
    // a new render function, defeating PluggableView's memo and re-rendering the whole
    // view subtree on every parent render.
    const { viewId, parentViewId, defaultPage } = declaration;
    const stableDeclaration = useMemo(() => ({ viewId, parentViewId, defaultPage }), [viewId, parentViewId, defaultPage]);
    useEffect(() => {
        // Register the view declaration
        registerPluggableViewDeclaration(stableDeclaration);
        // Cleanup: unregister on unmount
        return () => {
            unregisterPluggableView(viewId);
        };
    }, [stableDeclaration, viewId]);
    return useCallback((props) => _jsx(PluggableView, Object.assign({}, stableDeclaration, { props: Object.assign({}, props) })), [stableDeclaration]);
}
